import { Markdown } from '@/components/shared/Markdown';
import { SamIcon } from '@/components/shared/SamIcon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUser } from '@/hooks/useUser';
import { getInitials } from '@/utils/user';
import {
  ActionExecutionMessage,
  ResultMessage,
  Role,
  TextMessage,
} from '@copilotkit/runtime-client-gql';
import { motion } from 'framer-motion';
import { Check, Loader2, User } from 'lucide-react';

export const ActionBubble = ({
  message,
}: {
  message: ActionExecutionMessage;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-start items-start gap-3">
      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0  flex items-center justify-center">
        <SamIcon />
      </div>
      <div className="px-4 py-2 rounded-lg mb-2 bg-yellow-300 border opacity-50">
        <div className="flex items-center gap-3">
          {message.status?.code === 'Success' ? (
            <Check className="w-4 h-4" />
          ) : (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          <span>
            {message.status?.code === 'Success' ? 'Completed' : 'Processing...'}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export const ResultBubble = ({ message }: { message: ResultMessage }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-start items-start gap-3">
      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
        <SamIcon />
      </div>
      <div className="px-4 py-2 rounded-lg mb-2 bg-yellow-300 border opacity-50">
        <Markdown>{(message as any).result}</Markdown>
      </div>
    </motion.div>
  );
};

export const ChatBubble = ({ message }: { message: TextMessage }) => {
  const { data: user } = useUser();
  const isSystem = message.role === Role.System;
  const isAssistant = message.role === Role.Assistant;

  if (message.status.code !== 'Success' || message.content === '') {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex flex-row items-start gap-3 ${
        isSystem || isAssistant ? 'justify-start' : 'justify-end'
      }`}>
      {(isSystem || isAssistant) && (
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
          <SamIcon />
        </div>
      )}
      <div
        className={`px-4 py-2 rounded-lg mb-2 max-w-[80%] 2xl:max-w-[66%] inline-block ${
          isSystem || isAssistant
            ? 'bg-black text-white'
            : 'bg-muted text-muted-foreground'
        }`}>
        <Markdown inverted={isSystem || isAssistant}>
          {message.content}
        </Markdown>
      </div>
      {!isSystem && !isAssistant && (
        <>
          {user ? (
            <Avatar className="w-12 h-12 rounded-full overflow-hidden bg-primary items-center justify-center flex flex-shrink-0">
              {user?.image ? (
                <AvatarImage src={user.image} alt={user.email} />
              ) : (
                <AvatarFallback className="rounded-lg">
                  {getInitials(user?.name)}
                </AvatarFallback>
              )}
            </Avatar>
          ) : (
            <div className="w-12 h-12 rounded-full overflow-hidden bg-primary items-center justify-center flex flex-shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};
